import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import { ReactComponent as DataCollectEmptyIllustration } from '../../assets/svg/dataCollect-empty-state.svg';
import BotoneraModern from '../../components/Botonera/BotoneraModern.tsx';
import useNavVisibility from '../../hooks/useNavVisibility.ts';
import { useActionGuard } from '../../hooks/useActionGuard.ts';
import Breadcrumbs from '../../components/UI/Breadcrumbs/Breadcrumbs.tsx';
import CommerceSheetJobLoader, {
  COMMERCE_BUSY_PHASE,
} from '../../components/Comercios/CommerceSheetJobLoader/CommerceSheetJobLoader.tsx';
import { CommerceSheetReviewStep } from '../../components/Comercios/CommerceSheetReviewStep.tsx';
import reviewStepStyles from '../../components/Comercios/CommerceSheetReviewStep.module.scss';
import {
  buildCommerceMaterialRowFromManualMaterialSubmitPayload,
  buildRowsForCommerceSheetConfirm,
  computeUnchangedCommerceListingExclusionsFromParsedCategories,
  deleteCommerceSheetDraft,
  fetchCommerceCatalogMaterialsForPreviewListing,
  fetchCommerceCatalogMaterialsParsed,
  fetchMaterialSheetCategories,
  formatDocumentAssessmentForUser,
  getCommerceSheetDraft,
  initRematchMapForPreviewRows,
  isAcceptedPriceListFile,
  loadSheetConfirmSummary,
  buildSheetDraftRowsForPut,
  materialMatchTouchedMapAllFalse,
  materialMatchTouchedMapFromDraftRows,
  mergePreviewRowsWithFieldPatches,
  parseSheetConfirmResponse,
  postCommerceMaterialItemCreate,
  postCommerceSheetConfirm,
  putCommerceSheetDraft,
  sheetConfirmPollModalBody,
  priceListFileAccept,
  runCommerceSheetPreviewComplete,
  sheetRowFieldPatchAffectsCatalogMatch,
  splitSheetReviewExcludedFromLoadedRows,
  type SheetCategoryOption,
  type SheetConfirmJobPollUpdate,
  type ManualMaterialSubmitPayload,
  type SheetConfirmSubmitResult,
  type SheetRematchByRowIndex,
  type SheetRematchPick,
  type SheetRowFieldPatch,
} from '../../services/materialCommerceSheetService.ts';
import { type ParsedCategory } from '../../components/Perfil/PerfilMain/MisMaterialesComercio/commerceMaterialsModel.ts';
import {
  deleteCommerceMaterialItem,
  patchCommerceMaterialItem,
} from '../../services/commerceService.ts';
import { loadUserData } from '../../store/actions';
import { closeMainModal, openMainModal } from '../../store/actions/profileActions';
import { buildProfileUrl, getUserDisplayName } from '../../utils/navigationHelpers.ts';
import { normalizeUserCommerces } from '../../utils/userCommerces.ts';
import formStyles from '../../components/OnboardingV2/ConversationalOnboarding/CommerceOnboarding/CommerceForm.module.scss';
import styles from './CommerceAltaPage.module.scss';

type Phase = 'pick' | 'analyzing' | 'review';

const RESUME_SKELETON_ROW_KEYS = [0, 1, 2, 3, 4, 5, 6, 7] as const;

function reviewResumeSkeletonBarClass(row: number, col: number) {
  const w = [reviewStepStyles.resumeW1, reviewStepStyles.resumeW2, reviewStepStyles.resumeW3, reviewStepStyles.resumeW4];
  return w[(row * 2 + col) % 4];
}

const CommerceListaPreciosPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { checkAccess } = useActionGuard();
  const [searchParams, setSearchParams] = useSearchParams();
  const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility();
  const userData = useSelector((s: { general?: { userData?: Record<string, unknown> } }) => s.general?.userData);
  const [busy, setBusy] = useState(false);
  const [busyPhaseCode, setBusyPhaseCode] = useState('');
  const [busyIngestionLabel, setBusyIngestionLabel] = useState('');
  const [busyFileLabel, setBusyFileLabel] = useState<string | null>(null);
  const [priceSheet, setPriceSheet] = useState<File | null>(null);
  const [priceSheetError, setPriceSheetError] = useState('');
  const [isDragOverSheet, setIsDragOverSheet] = useState(false);
  const sheetInputRef = useRef<HTMLInputElement>(null);
  const priceSheetPendingAnalyzeRef = useRef<File | null>(null);

  const [phase, setPhase] = useState<Phase>('pick');
  const [previewRows, setPreviewRows] = useState<unknown[]>([]);
  const [categories, setCategories] = useState<SheetCategoryOption[]>([]);
  const [rematchByRowIndex, setRematchByRowIndex] = useState<SheetRematchByRowIndex>({});
  const [reviewError, setReviewError] = useState('');
  const [reviewRowPatches, setReviewRowPatches] = useState<Record<number, SheetRowFieldPatch>>({});
  const [reviewExcludedByRow, setReviewExcludedByRow] = useState<Record<number, boolean>>({});
  const [reviewMaterialsParsed, setReviewMaterialsParsed] = useState<ParsedCategory[] | null>(null);
  const [sheetRowTouchedForMatch, setSheetRowTouchedForMatch] = useState<Record<number, boolean>>({});
  const [draftOnServer, setDraftOnServer] = useState(false);
  const [isResumingDraft, setIsResumingDraft] = useState(false);
  const [reviewEntryMode, setReviewEntryMode] = useState<'sheet' | 'manual' | null>(null);
  const [commerceCatalogMutating, setCommerceCatalogMutating] = useState(false);

  const userId = userData?.id != null ? Number(userData.id) : 0;
  const commerces = useMemo(() => normalizeUserCommerces(userData), [userData]);
  const flexibleSheetEnabled = userData?.type !== 'JOBBOARD';

  const desdeAltaNotice = searchParams.get('desdeAlta') === '1';
  const resolvedInitialCommerceId = useMemo(() => {
    if (!commerces.length) return null;
    return commerces[0].id;
  }, [commerces]);

  const [selectedCommerceId, setSelectedCommerceId] = useState<number | null>(null);

  const syncListaPreciosFase = useCallback(
    (fase: 'analisis' | 'revision' | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (fase) next.set('fase', fase);
          else next.delete('fase');
          if (selectedCommerceId != null) next.set('commerceId', String(selectedCommerceId));
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams, selectedCommerceId]
  );

  useEffect(() => {
    setSelectedCommerceId(resolvedInitialCommerceId);
  }, [resolvedInitialCommerceId]);

  useLayoutEffect(() => {
    if (searchParams.get('fase') === 'analisis' && phase === 'pick') {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('fase');
          return next;
        },
        { replace: true }
      );
    }
  }, [phase, searchParams, setSearchParams]);

  useEffect(() => {
    if (!userId) return;
    if (commerces.length > 0) return;
    if (!checkAccess('register_commerce')) {
      navigate('/dashboard', { replace: true });
      return;
    }
    navigate('/comercios/alta', { replace: true });
  }, [checkAccess, commerces.length, navigate, userId]);

  useEffect(() => {
    if (!flexibleSheetEnabled || selectedCommerceId == null || phase !== 'pick') return;
    let cancelled = false;
    void (async () => {
      try {
        const d = await getCommerceSheetDraft(selectedCommerceId);
        if (!cancelled) setDraftOnServer(d != null && d.rows.length > 0);
      } catch {
        if (!cancelled) setDraftOnServer(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [flexibleSheetEnabled, phase, selectedCommerceId]);

  useEffect(() => {
    if (phase !== 'review' || selectedCommerceId == null || previewRows.length === 0) return;
    const rowsForPut = buildSheetDraftRowsForPut(
      previewRows,
      reviewRowPatches,
      reviewExcludedByRow,
      rematchByRowIndex,
      sheetRowTouchedForMatch
    );
    const t = window.setTimeout(() => {
      void putCommerceSheetDraft(selectedCommerceId, rowsForPut).catch(() => {});
    }, 2000);
    return () => window.clearTimeout(t);
  }, [
    phase,
    previewRows,
    rematchByRowIndex,
    reviewExcludedByRow,
    reviewRowPatches,
    selectedCommerceId,
    sheetRowTouchedForMatch,
  ]);

  const dismissDesdeAltaBanner = useCallback(() => {
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.delete('desdeAlta');
        return n;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const clearReviewState = useCallback(() => {
    setPhase('pick');
    syncListaPreciosFase(null);
    setPreviewRows([]);
    setCategories([]);
    setRematchByRowIndex({});
    setReviewError('');
    setReviewRowPatches({});
    setReviewExcludedByRow({});
    setReviewMaterialsParsed(null);
    setSheetRowTouchedForMatch({});
    setReviewEntryMode(null);
  }, [syncListaPreciosFase]);

  const runAnalyzeWithFile = useCallback(
    async (file: File) => {
      if (!flexibleSheetEnabled || selectedCommerceId == null) return;
      setPhase('analyzing');
      syncListaPreciosFase('analisis');
      setBusy(true);
      setBusyPhaseCode(COMMERCE_BUSY_PHASE.upload);
      setBusyIngestionLabel('');
      setBusyFileLabel(file.name);
      setReviewError('');
      const backToPick = () => {
        setPhase('pick');
        syncListaPreciosFase(null);
      };
      try {
        const parsed = await runCommerceSheetPreviewComplete(selectedCommerceId, file, {
          onUpdate: (u) => {
            setBusyPhaseCode(u.loaderPhase);
            setBusyIngestionLabel(u.ingestionProgressLabel?.trim() ?? '');
          },
        });
        if (parsed == null) {
          backToPick();
          dispatch(
            openMainModal({
              title: 'Cargar catálogo de materiales',
              body: 'No recibimos un resultado claro del análisis del archivo. Intentá de nuevo más tarde.',
              iconType: 'error',
              isSuccess: true,
              confirmHandler: null,
            })
          );
        } else if (parsed.status === 'rejected') {
          backToPick();
          const title = parsed.documentAssessment?.titleForUser?.trim() || 'Cargar catálogo de materiales';
          const detail = formatDocumentAssessmentForUser(parsed.documentAssessment);
          dispatch(
            openMainModal({
              title,
              body: `${detail} Podés probar con otro archivo.`,
              iconType: 'error',
              isSuccess: true,
              confirmHandler: null,
            })
          );
        } else if (parsed.rows.length === 0) {
          backToPick();
          dispatch(
            openMainModal({
              title: 'Cargar catálogo de materiales',
              body:
                parsed.status === 'ready'
                  ? 'No pudimos obtener filas del archivo para publicar. Revisá el archivo o probá con otro.'
                  : 'No se obtuvieron filas del archivo para este flujo.',
              iconType: 'error',
              isSuccess: true,
              confirmHandler: null,
            })
          );
        } else {
          const merged = mergePreviewRowsWithFieldPatches(parsed.rows, {});
          const [cats, parsedMat] = await Promise.all([
            fetchMaterialSheetCategories(),
            fetchCommerceCatalogMaterialsForPreviewListing(selectedCommerceId, merged).catch(
              (): ParsedCategory[] => []
            ),
          ]);
          setReviewMaterialsParsed(parsedMat);
          const autoExc = computeUnchangedCommerceListingExclusionsFromParsedCategories(
            merged,
            parsedMat
          );
          const rem = initRematchMapForPreviewRows(parsed.rows);
          setCategories(cats);
          setPreviewRows(parsed.rows);
          setRematchByRowIndex(rem);
          setReviewExcludedByRow(autoExc);
          setReviewRowPatches({});
          setSheetRowTouchedForMatch(materialMatchTouchedMapAllFalse(parsed.rows.length));
          setReviewEntryMode('sheet');
          setPhase('review');
          syncListaPreciosFase('revision');
        }
      } catch (sheetErr: unknown) {
        backToPick();
        const sheetMsg =
          (sheetErr as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          (sheetErr instanceof Error && sheetErr.message ? sheetErr.message : null) ||
          'No pudimos procesar el archivo.';
        dispatch(
          openMainModal({
            title: 'Cargar catálogo de materiales',
            body: `Hubo un problema al analizar el archivo: ${sheetMsg}`,
            iconType: 'error',
            isSuccess: true,
            confirmHandler: null,
          })
        );
      } finally {
        setBusy(false);
        setBusyPhaseCode('');
        setBusyIngestionLabel('');
        setBusyFileLabel(null);
      }
    },
    [dispatch, flexibleSheetEnabled, selectedCommerceId, syncListaPreciosFase]
  );

  useEffect(() => {
    if (!flexibleSheetEnabled || selectedCommerceId == null) return;
    const pending = priceSheetPendingAnalyzeRef.current;
    if (!pending) return;
    priceSheetPendingAnalyzeRef.current = null;
    void runAnalyzeWithFile(pending);
  }, [flexibleSheetEnabled, selectedCommerceId, runAnalyzeWithFile]);

  const applyPriceSheetFile = useCallback(
    (file: File | null) => {
      setPriceSheetError('');
      clearReviewState();
      if (!file) {
        priceSheetPendingAnalyzeRef.current = null;
        setPriceSheet(null);
        return;
      }
      if (!isAcceptedPriceListFile(file)) {
        priceSheetPendingAnalyzeRef.current = null;
        setPriceSheet(null);
        setPriceSheetError(
          'Formato no admitido. Podés subir PDF, imagen (JPEG, PNG, WebP), texto plano, JSON, CSV o Excel (XLS/XLSX).'
        );
        return;
      }
      setPriceSheet(file);
      if (flexibleSheetEnabled) {
        if (selectedCommerceId != null) {
          void runAnalyzeWithFile(file);
        } else {
          priceSheetPendingAnalyzeRef.current = file;
        }
      }
    },
    [clearReviewState, flexibleSheetEnabled, runAnalyzeWithFile, selectedCommerceId]
  );

  const onHiddenSheetChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0] ?? null;
      applyPriceSheetFile(f);
      e.target.value = '';
    },
    [applyPriceSheetFile]
  );

  const onSheetDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOverSheet(true);
  }, []);

  const onSheetDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const next = e.relatedTarget as Node | null;
    if (next && e.currentTarget.contains(next)) return;
    setIsDragOverSheet(false);
  }, []);

  const onSheetDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onSheetDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOverSheet(false);
      const f = e.dataTransfer.files?.[0];
      if (f) applyPriceSheetFile(f);
    },
    [applyPriceSheetFile]
  );

  const goProfile = useCallback(() => {
    dispatch(loadUserData());
    const name = getUserDisplayName(userData);
    navigate(
      buildProfileUrl(String(userId), name, 'Gestión', {
        section: 'comercio',
        commerceId: selectedCommerceId ?? undefined,
      })
    );
  }, [dispatch, navigate, selectedCommerceId, userData, userId]);

  const patchReviewRowField = useCallback((rowIndex: number, patch: Partial<SheetRowFieldPatch>) => {
    setReviewRowPatches((prev) => ({
      ...prev,
      [rowIndex]: { ...prev[rowIndex], ...patch },
    }));
    if (sheetRowFieldPatchAffectsCatalogMatch(patch)) {
      setSheetRowTouchedForMatch((prev) => ({ ...prev, [rowIndex]: true }));
    }
  }, []);

  const setReviewRowExcluded = useCallback((rowIndex: number, excluded: boolean) => {
    setReviewExcludedByRow((prev) => {
      const next = { ...prev };
      if (excluded) next[rowIndex] = true;
      else delete next[rowIndex];
      return next;
    });
  }, []);

  const submitManualCatalogItem = useCallback(
    async (payload: ManualMaterialSubmitPayload): Promise<{ ok: boolean; message?: string }> => {
      if (selectedCommerceId == null) {
        return { ok: false, message: 'No hay comercio seleccionado.' };
      }
      try {
        let row: Record<string, unknown>;
        try {
          row = buildCommerceMaterialRowFromManualMaterialSubmitPayload(payload);
        } catch (e: unknown) {
          return {
            ok: false,
            message: e instanceof Error ? e.message : 'No se pudo preparar el alta.',
          };
        }
        const res = await postCommerceMaterialItemCreate(selectedCommerceId, { rows: [row] });
        const parsed = parseSheetConfirmResponse(res.data);
        const r0 = parsed?.results?.[0];
        if (r0 && r0.outcome.trim().toLowerCase() === 'failed') {
          return { ok: false, message: r0.reason || 'No se pudo registrar el material.' };
        }
        if (parsed?.failed?.length) {
          return { ok: false, message: 'No se pudo registrar el material.' };
        }
        const merged = mergePreviewRowsWithFieldPatches(previewRows, reviewRowPatches);
        try {
          if (previewRows.length === 0) {
            setReviewMaterialsParsed(await fetchCommerceCatalogMaterialsParsed(selectedCommerceId));
          } else {
            setReviewMaterialsParsed(
              await fetchCommerceCatalogMaterialsForPreviewListing(selectedCommerceId, merged)
            );
          }
        } catch {
          setReviewMaterialsParsed([]);
        }
        dispatch(loadUserData());
        return { ok: true };
      } catch (e: unknown) {
        if (e instanceof Error && e.message) {
          return { ok: false, message: e.message };
        }
        if (isAxiosError(e)) {
          const msg =
            (typeof e.response?.data === 'object' &&
              e.response?.data &&
              'message' in e.response.data &&
              String((e.response.data as { message?: string }).message)) ||
            e.message;
          return { ok: false, message: msg || 'No pudimos registrar el material.' };
        }
        return { ok: false, message: 'No pudimos registrar el material.' };
      }
    },
    [dispatch, previewRows, reviewRowPatches, selectedCommerceId]
  );

  const handleCommerceCatalogEditItem = useCallback(
    async (
      itemId: number,
      patch: {
        price: number | null;
        unquoted: boolean;
        valueAddedTax: boolean;
        promotion: string;
        promotionDiscount: number | null;
      }
    ) => {
      if (selectedCommerceId == null) return;
      setCommerceCatalogMutating(true);
      try {
        await patchCommerceMaterialItem(selectedCommerceId, itemId, {
          price: patch.price ?? undefined,
          unquoted: patch.unquoted,
          valueAddedTax: patch.unquoted ? false : patch.valueAddedTax,
          promotion: patch.promotion.trim() ? patch.promotion.trim() : null,
          promotionDiscount: patch.promotionDiscount,
        });
        try {
          if (previewRows.length === 0) {
            setReviewMaterialsParsed(await fetchCommerceCatalogMaterialsParsed(selectedCommerceId));
          } else {
            const merged = mergePreviewRowsWithFieldPatches(previewRows, reviewRowPatches);
            setReviewMaterialsParsed(
              await fetchCommerceCatalogMaterialsForPreviewListing(selectedCommerceId, merged)
            );
          }
        } catch {
          setReviewMaterialsParsed([]);
        }
        dispatch(loadUserData());
      } finally {
        setCommerceCatalogMutating(false);
      }
    },
    [dispatch, previewRows, reviewRowPatches, selectedCommerceId]
  );

  const handleCommerceCatalogDeleteItem = useCallback(
    async (itemId: number) => {
      if (selectedCommerceId == null) return;
      setCommerceCatalogMutating(true);
      try {
        await deleteCommerceMaterialItem(selectedCommerceId, itemId);
        try {
          if (previewRows.length === 0) {
            setReviewMaterialsParsed(await fetchCommerceCatalogMaterialsParsed(selectedCommerceId));
          } else {
            const merged = mergePreviewRowsWithFieldPatches(previewRows, reviewRowPatches);
            setReviewMaterialsParsed(
              await fetchCommerceCatalogMaterialsForPreviewListing(selectedCommerceId, merged)
            );
          }
        } catch {
          setReviewMaterialsParsed([]);
        }
        dispatch(loadUserData());
      } finally {
        setCommerceCatalogMutating(false);
      }
    },
    [dispatch, previewRows, reviewRowPatches, selectedCommerceId]
  );

  const startManualCatalogSession = useCallback(async () => {
    if (!flexibleSheetEnabled || selectedCommerceId == null || busy) return;
    setReviewError('');
    setBusy(true);
    try {
      const cats = await fetchMaterialSheetCategories();
      setCategories(cats);
      setPreviewRows([]);
      setRematchByRowIndex({});
      setReviewExcludedByRow({});
      setReviewRowPatches({});
      setSheetRowTouchedForMatch({});
      try {
        setReviewMaterialsParsed(await fetchCommerceCatalogMaterialsParsed(selectedCommerceId));
      } catch {
        setReviewMaterialsParsed([]);
      }
      setReviewEntryMode('manual');
      setPhase('review');
      syncListaPreciosFase('revision');
    } catch {
      dispatch(
        openMainModal({
          title: 'Cargar catálogo de materiales',
          body: 'No pudimos preparar el formulario. Intentá de nuevo en unos minutos.',
          iconType: 'error',
          isSuccess: true,
          confirmHandler: null,
        })
      );
    } finally {
      setBusy(false);
    }
  }, [busy, dispatch, flexibleSheetEnabled, selectedCommerceId, syncListaPreciosFase]);

  const patchRematch = useCallback((rowIndex: number, patch: Partial<SheetRematchPick>) => {
    setRematchByRowIndex((prev) => {
      const cur = prev[rowIndex] ?? {
        userCategoryId: '' as const,
        rawName: '',
        createCatalogMaterial: false,
        rejectSuggestedCatalogMatch: false,
      };
      return {
        ...prev,
        [rowIndex]: {
          userCategoryId: cur.userCategoryId,
          rawName: cur.rawName,
          createCatalogMaterial: cur.createCatalogMaterial === true,
          rejectSuggestedCatalogMatch: cur.rejectSuggestedCatalogMatch === true,
          ...patch,
        },
      };
    });
    setSheetRowTouchedForMatch((prev) => ({ ...prev, [rowIndex]: true }));
  }, []);

  const handleReviewSuccessContinue = useCallback(() => {
    clearReviewState();
    goProfile();
  }, [clearReviewState, goProfile]);

  const resumeDraft = useCallback(async () => {
    if (selectedCommerceId == null) {
      return;
    }
    flushSync(() => {
      setIsResumingDraft(true);
    });
    try {
      const [d, cats] = await Promise.all([
        getCommerceSheetDraft(selectedCommerceId),
        fetchMaterialSheetCategories(),
      ]);
      if (!d?.rows.length) {
        setDraftOnServer(false);
        return;
      }
      setCategories(cats);
      const split = splitSheetReviewExcludedFromLoadedRows(d.rows);
      setPreviewRows(split.rows);
      const initRem = initRematchMapForPreviewRows(split.rows);
      setRematchByRowIndex(
        d.rematchByRowIndex && Object.keys(d.rematchByRowIndex).length > 0
          ? { ...initRem, ...d.rematchByRowIndex }
          : initRem
      );
      setReviewExcludedByRow(split.excludedByRowIndex);
      setReviewRowPatches({});
      setSheetRowTouchedForMatch(materialMatchTouchedMapFromDraftRows(split.rows));
      setReviewError('');
      try {
        const merged = mergePreviewRowsWithFieldPatches(split.rows, {});
        setReviewMaterialsParsed(
          await fetchCommerceCatalogMaterialsForPreviewListing(selectedCommerceId, merged)
        );
      } catch (e) {
        setReviewMaterialsParsed([]);
      }
      setReviewEntryMode('sheet');
      setPhase('review');
      syncListaPreciosFase('revision');
    } catch (e) {
      dispatch(
        openMainModal({
          title: 'Cargar catálogo de materiales',
          body: 'No pudimos recuperar el borrador guardado.',
          iconType: 'error',
          isSuccess: true,
          confirmHandler: null,
        })
      );
    } finally {
      setIsResumingDraft(false);
    }
  }, [dispatch, selectedCommerceId, syncListaPreciosFase]);

  const discardServerDraft = useCallback(async () => {
    if (selectedCommerceId == null) return;
    await deleteCommerceSheetDraft(selectedCommerceId).catch(() => {});
    setDraftOnServer(false);
  }, [selectedCommerceId]);

  const dispatchCommerceSheetConfirmPollModal = useCallback(
    (u: SheetConfirmJobPollUpdate) => {
      dispatch(
        openMainModal({
          isLoading: true,
          title: 'Publicando listado',
          body: sheetConfirmPollModalBody(u),
          isSuccess: true,
          noActionNeeded: true,
        })
      );
    },
    [dispatch]
  );

  const handleConfirmFromReview = useCallback(async (): Promise<SheetConfirmSubmitResult> => {
    if (selectedCommerceId == null) {
      return { ok: false, message: 'No hay comercio seleccionado.' };
    }
    setReviewError('');
    const mergedRows = mergePreviewRowsWithFieldPatches(previewRows, reviewRowPatches);
    const { error } = buildRowsForCommerceSheetConfirm(mergedRows, rematchByRowIndex, reviewExcludedByRow);
    if (error) {
      setReviewError(error);
      return { ok: false, message: error };
    }
    dispatch(
      openMainModal({
        isLoading: true,
        title: 'Publicando listado',
        body: 'Guardando tu listado…',
        isSuccess: true,
        noActionNeeded: true,
      })
    );
    try {
      await putCommerceSheetDraft(
        selectedCommerceId,
        buildSheetDraftRowsForPut(
          previewRows,
          reviewRowPatches,
          reviewExcludedByRow,
          rematchByRowIndex,
          sheetRowTouchedForMatch
        )
      );
      dispatch(
        openMainModal({
          isLoading: true,
          title: 'Publicando listado',
          body: 'Listo. Publicando tus productos…',
          isSuccess: true,
          noActionNeeded: true,
        })
      );
      const sheetConfirmRes = await postCommerceSheetConfirm(selectedCommerceId, [], {
        fromSavedDraft: true,
        onConfirmPollUpdate: dispatchCommerceSheetConfirmPollModal,
      });
      await deleteCommerceSheetDraft(selectedCommerceId).catch(() => {});
      dispatch(loadUserData());
      const summary = await loadSheetConfirmSummary(selectedCommerceId, sheetConfirmRes.data);
      return { ok: true, summary };
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e instanceof Error ? e.message : null) ||
        'No pudimos guardar el listado.';
      setReviewError(msg);
      return { ok: false, message: msg };
    } finally {
      dispatch(closeMainModal());
    }
  }, [
    dispatch,
    dispatchCommerceSheetConfirmPollModal,
    previewRows,
    rematchByRowIndex,
    reviewExcludedByRow,
    reviewRowPatches,
    selectedCommerceId,
    sheetRowTouchedForMatch,
  ]);

  const handleBackFromReview = useCallback(() => {
    clearReviewState();
  }, [clearReviewState]);

  const handleReanalyzeCurrentFile = useCallback(() => {
    if (priceSheet == null || selectedCommerceId == null || busy) return;
    void runAnalyzeWithFile(priceSheet);
  }, [busy, priceSheet, runAnalyzeWithFile, selectedCommerceId]);

  if (!userId) {
    return null;
  }

  if (commerces.length === 0) {
    return null;
  }

  const selectedCommerce = commerces.find((c) => c.id === selectedCommerceId) ?? commerces[0];

  return (
    <div className={styles.container}>
      {ocultarEnMobile && <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} />}
      <div className={styles.wrapper}>
        <div className={styles.breadcrumbBar}>
          <div className={styles.breadcrumbBarStart}>
            <Breadcrumbs />
          </div>
          <button type="button" className={styles.breadcrumbProfileBack} onClick={goProfile}>
            Volver al perfil
          </button>
        </div>
        <main className={styles.main}>
          {isResumingDraft ? (
            <div
              className={`${reviewStepStyles.wrap} ${reviewStepStyles.wrapReviewDocked}`}
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              <div className={reviewStepStyles.reviewScrollBlock}>
                <h2 className={reviewStepStyles.title}>Revisión antes de guardar</h2>
                <p className={reviewStepStyles.intro}>
                  Mirá cómo quedó tu listado y ajustá lo que haga falta. Cuando esté bien para vos, publicalo en tu perfil.
                </p>
                <div className={reviewStepStyles.sheetTopBar}>
                  <p className={reviewStepStyles.pendingBanner}>
                    <Loader2 className={reviewStepStyles.resumeLoaderSpin} size={16} aria-hidden />
                    Cargando las filas del borrador y los datos de tu comercio…
                  </p>
                </div>
                <div className={reviewStepStyles.tableWrap}>
                  <table className={reviewStepStyles.table}>
                    <colgroup>
                      <col className={reviewStepStyles.colItem} />
                      <col className={reviewStepStyles.colEstado} />
                      <col className={reviewStepStyles.colCategoria} />
                      <col className={reviewStepStyles.colDetalle} />
                      <col className={reviewStepStyles.colPrecio} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className={`${reviewStepStyles.th} ${reviewStepStyles.thItem}`} scope="col">
                          Ítem
                        </th>
                        <th className={reviewStepStyles.th}>Estado</th>
                        <th className={reviewStepStyles.th}>Categoría</th>
                        <th className={reviewStepStyles.th}>Detalle</th>
                        <th className={reviewStepStyles.th} scope="col" aria-describedby="sheet-price-footnote-resume">
                          Precio de lista<sup className={reviewStepStyles.thAsterisk}>*</sup>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {RESUME_SKELETON_ROW_KEYS.map((i) => (
                        <tr key={i}>
                          <td className={reviewStepStyles.tdItem}>
                            <span
                              className={`${reviewStepStyles.resumeSkeletonBar} ${reviewStepStyles.resumeSkelItemBar}`}
                            />
                          </td>
                          <td className={reviewStepStyles.td}>
                            <span
                              className={`${reviewStepStyles.resumeSkeletonBar} ${reviewResumeSkeletonBarClass(i, 1)}`}
                            />
                          </td>
                          <td className={reviewStepStyles.td}>
                            <span
                              className={`${reviewStepStyles.resumeSkeletonBar} ${reviewResumeSkeletonBarClass(i, 2)}`}
                            />
                          </td>
                          <td className={reviewStepStyles.td}>
                            <span
                              className={`${reviewStepStyles.resumeSkeletonBar} ${reviewResumeSkeletonBarClass(i, 3)}`}
                            />
                          </td>
                          <td className={reviewStepStyles.td}>
                            <span
                              className={`${reviewStepStyles.resumeSkeletonBar} ${reviewStepStyles.resumeSkelPriceBar}`}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p id="sheet-price-footnote-resume" className={reviewStepStyles.tableFootnote}>
                  <span className={reviewStepStyles.footnoteMark}>*</span> Los precios que figuren en la tabla son
                  meramente informativos y optativos. Edify no los presenta como oferta real ni como compromiso comercial
                  por parte del comercio.
                </p>
              </div>
            </div>
          ) : phase === 'review' ? (
            <CommerceSheetReviewStep
              previewRows={previewRows}
              categories={categories}
              rematchByRowIndex={rematchByRowIndex}
              rowFieldPatches={reviewRowPatches}
              onRowFieldPatch={patchReviewRowField}
              excludedByRowIndex={reviewExcludedByRow}
              onExcludedChange={setReviewRowExcluded}
              onAddManualRow={submitManualCatalogItem}
              onRematchChange={patchRematch}
              onConfirm={handleConfirmFromReview}
              onSuccessContinue={handleReviewSuccessContinue}
              onBack={handleBackFromReview}
              errorText={reviewError || null}
              commerceId={selectedCommerceId}
              commerceMaterialsCategories={reviewMaterialsParsed ?? undefined}
              manualCatalogEntry={reviewEntryMode === 'manual'}
              onCommerceCatalogEditItem={handleCommerceCatalogEditItem}
              onCommerceCatalogDeleteItem={handleCommerceCatalogDeleteItem}
              commerceCatalogActionsBusy={commerceCatalogMutating}
            />
          ) : (
            <div className={styles.commerceStack}>
              <div className={styles.commerceHero}>
                <div className={styles.commerceHeroIntro}>
                  <h1 className={styles.commerceHeroTitle}>Cargar catálogo de materiales</h1>
                  <div className={styles.sheetIntro}>
                    <p className={styles.subtitle}>
                      Subí un listado o planilla (o PDF) para <strong>{selectedCommerce.name}</strong>: el asistente
                      interpreta el archivo, vos revisás la tabla y publicás cuando esté bien.
                    </p>
                    <ul className={styles.sheetValueList}>
                      <li>El archivo puede incluir precios, productos y unidades según el formato.</li>
                      <li>
                        Lo publicado queda en el perfil de <strong>{selectedCommerce.name}</strong> y visible en Edify.
                      </li>
                      <li>Podés recibir avisos cuando alguien busca esos materiales.</li>
                      <li>Después del análisis revisás filas, ajustás lo necesario y confirmás.</li>
                    </ul>
                  </div>
                </div>
                {desdeAltaNotice && phase === 'pick' && !busy ? (
                  <div className={styles.commerceHeroDraft}>
                    <div className={styles.desdeAltaBanner} role="status">
                      <p className={styles.desdeAltaBannerBody}>
                        <strong>Tu comercio ya está dado de alta en Edify.</strong> Para que tus productos aparezcan en
                        la plataforma tenés que cargar tu lista de materiales (precios y productos): podés hacerlo acá
                        con un archivo o PDF cuando quieras, o más tarde desde tu perfil o la gestión del comercio.
                      </p>
                      <button type="button" className={styles.desdeAltaBannerDismiss} onClick={dismissDesdeAltaBanner}>
                        Entendido
                      </button>
                    </div>
                  </div>
                ) : null}
                {draftOnServer ? (
                  <div className={styles.commerceHeroDraft}>
                    <div className={styles.sheetDraftBar} role="region" aria-label="Borrador de listado">
                      <div className={styles.sheetDraftRow}>
                        <p className={styles.sheetDraftText}>
                          <strong>Borrador guardado.</strong> Hay una edición anterior sin publicar para este comercio.
                        </p>
                        <div className={styles.sheetDraftActions}>
                          <button type="button" className={styles.sheetDraftSecondary} onClick={() => void discardServerDraft()}>
                            Descartar borrador
                          </button>
                          <button
                            type="button"
                            className={styles.sheetDraftPrimary}
                            onClick={() => {
                              void resumeDraft();
                            }}
                          >
                            Continuar edición
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className={styles.commerceMain}>
                {phase === 'analyzing' ? (
                  <div className={styles.commerceListaAnalyzing}>
                    <p className={styles.commerceListaAnalyzingKicker}>
                      {selectedCommerce?.name ? (
                        <>
                          <strong>{selectedCommerce.name}</strong> · preparando el listado
                        </>
                      ) : (
                        'Preparando el listado'
                      )}
                    </p>
                    <h2 className={styles.commerceListaAnalyzingTitle}>Analizando el archivo</h2>
                    <p className={styles.commerceListaAnalyzingFile}>
                      {busyFileLabel ? (
                        <>
                          <span className={styles.commerceListaAnalyzingFileLabel}>Archivo</span> {busyFileLabel}
                        </>
                      ) : null}
                    </p>
                    <div className={styles.commerceListaAnalyzingLoader}>
                      <CommerceSheetJobLoader
                        layout="inline"
                        fileLabel={null}
                        phaseCode={busyPhaseCode}
                        progressDetail={busyIngestionLabel}
                        inlineArtwork={
                          <DataCollectEmptyIllustration className={styles.commerceListaPickEmptySvg} aria-hidden />
                        }
                      />
                    </div>
                  </div>
                ) : (
                <>
                {!flexibleSheetEnabled ? (
                  <>
                    <p className={styles.subtitle}>
                      Con tu tipo de cuenta la carga de planilla flexible no está disponible en la API. Si necesitás
                      ayuda, contactá al equipo de Edify.
                    </p>
                    <div className={formStyles.actions} style={{ marginTop: '1rem' }}>
                      <button type="button" className={formStyles.submit} onClick={goProfile}>
                        Volver al perfil
                      </button>
                    </div>
                  </>
                ) : (
                  <section className={formStyles.sheetSection} aria-labelledby="lista-precios-sheet-heading">
                    <h2 id="lista-precios-sheet-heading" className={formStyles.sheetTitle}>
                      Archivo
                    </h2>

                    <input
                      ref={sheetInputRef}
                      type="file"
                      accept={priceListFileAccept}
                      className={formStyles.sheetInputHidden}
                      onChange={onHiddenSheetChange}
                      disabled={busy}
                    />
                    <div
                      className={`${formStyles.pickDropZone} ${!busy && isDragOverSheet ? formStyles.pickDropZoneDragOver : ''}`}
                      {...(busy
                        ? {}
                        : {
                            onDragEnter: onSheetDragEnter,
                            onDragLeave: onSheetDragLeave,
                            onDragOver: onSheetDragOver,
                            onDrop: onSheetDrop,
                          })}
                    >
                      {priceSheet ? (
                        <div className={styles.commerceListaPickSplit}>
                          <div className={styles.commerceListaPickSplitArt}>
                            <DataCollectEmptyIllustration className={styles.commerceListaPickEmptySvg} aria-hidden />
                          </div>
                          <div className={styles.commerceListaPickSplitMain}>
                            <p className={`${formStyles.pickDropHint} ${styles.commerceListaPickSplitHint}`}>
                              Seguís con <strong>{priceSheet.name}</strong>. Podés volver a analizarlo para regenerar la tabla, o
                              elegir otro archivo (arrastrá acá o usá &quot;Cambiar archivo&quot;).
                            </p>
                            <div className={styles.commerceListaPickActionRow}>
                              <button
                                type="button"
                                className={formStyles.pickPrimaryBtn}
                                disabled={selectedCommerceId == null}
                                onClick={() => void handleReanalyzeCurrentFile()}
                              >
                                Volver a analizar
                              </button>
                              <button type="button" className={formStyles.sheetFileClear} onClick={() => sheetInputRef.current?.click()}>
                                Cambiar archivo
                              </button>
                              <button type="button" className={formStyles.cancel} onClick={() => applyPriceSheetFile(null)}>
                                Quitar
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className={styles.commerceListaPickSplit}>
                          <div className={styles.commerceListaPickSplitArt}>
                            <DataCollectEmptyIllustration className={styles.commerceListaPickEmptySvg} aria-hidden />
                          </div>
                          <div className={styles.commerceListaPickSplitMain}>
                            <p className={`${formStyles.pickDropHint} ${styles.commerceListaPickSplitHint}`}>
                              Arrastrá y soltá acá o usá &quot;Elegir archivo&quot;. Formatos: PDF, imagen, CSV o
                              Excel. Con un archivo válido, la IA de Edify lo interpreta para armar la tabla de precios;
                              puede tardar un poco y después revisás todo antes de publicar. Si preferís cargar de a
                              uno sin planilla, usá &quot;Agregar materiales a mano&quot;.
                            </p>
                            <div className={styles.commerceListaPickActionRow}>
                              <button
                                type="button"
                                className={formStyles.pickPrimaryBtn}
                                onClick={() => sheetInputRef.current?.click()}
                              >
                                Elegir archivo
                              </button>
                              <button
                                type="button"
                                className={formStyles.cancel}
                                disabled={selectedCommerceId == null || busy}
                                onClick={() => void startManualCatalogSession()}
                              >
                                Agregar materiales a mano
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    {priceSheetError && !busy ? <p className={formStyles.sheetFileError}>{priceSheetError}</p> : null}
                  </section>
                )}
                </>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default CommerceListaPreciosPage;
